/**
 * Route Helper Utilities
 *
 * Pure functions for constructing route paths based on session types and route configurations.
 * These utilities ensure consistent path generation across the application.
 */

import type { SessionType } from "@/types"

/**
 * Route configuration from the routes context.
 * This is a minimal subset needed for path construction.
 */
export type RouteConfig = {
  id: string
  path: string
}

/**
 * Constructs the base path for project views based on the session type.
 *
 * All modules use nested routes like /explorer/projects, /custom-variant/projects.
 * Falls back to /explorer/projects for unknown session types.
 *
 * @param sessionType - Session type identifier
 * @param routes - Array of route configurations from the routes context
 * @returns The base path for projects (e.g., "/explorer/projects")
 *
 * @example
 * const routes = [{ id: "open", path: "/explorer" }, { id: "custom-variant", path: "/custom-variant" }]
 *
 * getProjectBasePath("open", routes)
 * // Returns: "/explorer/projects"
 *
 * getProjectBasePath("custom-variant", routes)
 * // Returns: "/custom-variant/projects"
 *
 * getProjectBasePath("unknown", routes)
 * // Returns: "/explorer/projects" (fallback)
 */
export function getProjectBasePath(sessionType: SessionType, routes: RouteConfig[]): string {
  // The standalone "projects" route IS the projects list — don't append /projects
  if (sessionType === "projects") return "/projects"
  const route = routes.find((r) => r.id === sessionType)
  if (route) {
    return `${route.path}/projects`
  }
  // Fallback to /explorer/projects
  return "/explorer/projects"
}

/**
 * Returns the path to the projects list for the given session type.
 * Use this when navigating away from a project (e.g. after leaving) to land on the correct list.
 *
 * - Standalone projects route (sessionType "projects"): returns "/projects"
 * - Nested module routes (e.g. "open"): returns "/explorer/projects"
 */
export function getProjectsListPath(sessionType: SessionType, routes: RouteConfig[]): string {
  return getProjectBasePath(sessionType, routes)
}
